// Keeps asynchronous views honest in an app whose router simply empties the content root on navigation.
//
// The router gives views no teardown hook, so a response that arrives after the person moved on would
// paint into a detached (or worse, a newly mounted) view. A lifecycle ties every request to (a) the
// view still being mounted and (b) being the newest request of its kind.

export interface Lifecycle {
  /** True while this view's marker is still in the document (the router has not replaced it). */
  alive(): boolean;
  /** Starts a request slot: aborts the previous one and returns a handle to check before painting. */
  begin(): Request;
  /** Aborts everything and stops listening. Safe to call repeatedly. */
  dispose(): void;
}

export interface Request {
  signal: AbortSignal;
  /** True only if the view is still mounted AND no newer request has begun. */
  current(): boolean;
}

export function createLifecycle(marker: HTMLElement): Lifecycle {
  let latest = 0;
  let controller: AbortController | null = null;
  let disposed = false;

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    controller?.abort();
    window.removeEventListener("hashchange", onHashChange);
  };
  // Leaving the page aborts in-flight work immediately, instead of waiting for the response to arrive.
  const onHashChange = () => {
    if (!marker.isConnected) dispose();
  };
  window.addEventListener("hashchange", onHashChange);

  return {
    alive: () => !disposed && marker.isConnected,
    begin() {
      controller?.abort();
      const mine = ++latest;
      const own = new AbortController();
      controller = own;
      return {
        signal: own.signal,
        current: () => !disposed && marker.isConnected && mine === latest && !own.signal.aborted,
      };
    },
    dispose,
  };
}

/** AbortError from a superseded or abandoned request: expected, never shown as a failure. */
export function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}
